import React, { useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { vizMarkdownComponents } from '../utils/markdownComponents';


/**
 * Unwrap an agent output value to a human-readable string.
 *
 * Agent nodes return an A2A message envelope:
 *   { parts: [{ kind: 'text', text: '{"reports":{"aeri":"...","capo":"..."}}' }] }
 *
 * This function drills through:
 *   1. A2A envelope  →  parts[].text
 *   2. Inner JSON string  →  reports dict values (joined)
 *   3. Plain string / null  →  returned as-is
 *
 * Returns { text: string | null, agentName: string | null }
 * where agentName is set when an agent_type field is found.
 */
function unwrapAgentValue(value) {
  if (value == null) return { text: null, agentName: null };

  if (typeof value === 'string') {
    if (value.startsWith('[skipped —')) {
      return { text: value, agentName: null };
    }
    try {
      return unwrapAgentValue(JSON.parse(value));
    } catch {
      return { text: value, agentName: null };
    }
  }

  if (typeof value !== 'object') {
    return { text: String(value), agentName: null };
  }

  /* A2A message envelope */
  if (Array.isArray(value.parts)) {
    const textParts = value.parts
      .filter((p) => p.kind === 'text' && p.text)
      .map((p) => {
        try {
          const inner = JSON.parse(p.text);
          /* Agent stack response: { agent_type, query, reports: { name: text } } */
          if (inner.reports && typeof inner.reports === 'object') {
            const entries = Object.entries(inner.reports);
            if (entries.length === 1) return entries[0][1];
            return entries
              .map(([name, body]) => `**${name}**\n\n${body}`)
              .join('\n\n---\n\n');
          }
          /* Fallback: stringify inner if no reports key */
          return JSON.stringify(inner, null, 2);
        } catch {
          return p.text;
        }
      });
    const agentName =
      value.parts.reduce((acc, p) => {
        if (acc) return acc;
        try {
          const inner = JSON.parse(p.text || '');
          return inner.agent_type || null;
        } catch {
          return null;
        }
      }, null);
    return {
      text: textParts.join('\n\n') || null,
      agentName,
    };
  }

  /* Generic object fallback */
  return { text: JSON.stringify(value, null, 2), agentName: null };
}

/* ─── Copy-to-clipboard button ───────────────────────────────── */

/**
 * Button that writes text to the system clipboard with a transient
 * "Copied!" confirmation.
 *
 * @param {string} text - Plain text to copy.
 */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      title="Copy to clipboard"
      style={{
        padding: '2px 7px',
        fontSize: '0.65rem',
        border: '1px solid var(--border-color)',
        borderRadius: 4,
        background: 'transparent',
        color: copied
          ? 'var(--success-text-color, #065f46)'
          : 'var(--text-secondary)',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

/* ─── Result tab — human-readable output values ─────────────── */

function ResultView({ output }) {
  if (!output || Object.keys(output).length === 0) {
    return (
      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
        No output captured.
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {Object.entries(output).map(([key, value]) => {
        const { text, agentName } = unwrapAgentValue(value);
        const isSoftFail =
          typeof text === 'string' && text.startsWith('[skipped —');
        const isNull = text == null;
        return (
          <div key={key}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {key}
                {agentName && (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: '0.68rem',
                      color: 'var(--text-secondary)',
                      opacity: 0.7,
                      textTransform: 'none',
                      letterSpacing: 0,
                    }}
                  >
                    via {agentName}
                  </span>
                )}
              </div>
              {!isNull && text && <CopyButton text={text} />}
            </div>
            <div
              style={{
                color:
                  isSoftFail || isNull
                    ? 'var(--text-secondary)'
                    : 'var(--text-color)',
                fontStyle: isNull ? 'italic' : 'normal',
                padding: '6px 8px',
                background: isSoftFail
                  ? 'rgba(234,179,8,0.07)'
                  : isNull
                    ? 'transparent'
                    : 'var(--panel-background)',
                border: isSoftFail
                  ? '1px solid rgba(234,179,8,0.25)'
                  : '1px solid var(--border-color)',
                borderRadius: 4,
              }}
            >
              {isNull ? (
                <span style={{ fontSize: '0.82rem' }}>(no output)</span>
              ) : isSoftFail ? (
                <span style={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>
                  {text}
                </span>
              ) : (
                <div className="wf-run-markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeSanitize]}
                    components={vizMarkdownComponents}
                  >
                    {text}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


export { ResultView };
